import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import posthog from 'posthog-js';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    posthog.capture('app_crash', {
      error: error.message,
      componentStack: info.componentStack,
    });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl p-10 text-center space-y-8">
          <div className="w-16 h-16 bg-red-50 rounded-[1.5rem] flex items-center justify-center mx-auto">
            <AlertTriangle size={32} className="text-red-400" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-black uppercase italic text-slate-900 tracking-tight">
              Something went wrong
            </h2>
            <p className="text-sm text-slate-500 font-medium leading-relaxed">
              This has been reported and we&apos;re on it. Try refreshing — if the problem
              persists, contact support@scoutloop.app.
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-amber-600 text-white py-4 rounded-2xl font-black uppercase italic text-xs tracking-widest hover:bg-amber-700 transition active:scale-95 flex items-center justify-center gap-2"
            >
              <RefreshCw size={14} /> Try Again
            </button>
            <button
              onClick={() => {
                window.location.href = '/';
              }}
              className="w-full bg-slate-50 text-slate-500 py-4 rounded-2xl font-black uppercase italic text-xs tracking-widest hover:bg-slate-100 transition active:scale-95 flex items-center justify-center gap-2"
            >
              <Home size={14} /> Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
